import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiDelete } from "../../utils/apiClient";
import { useUserContext } from "../../contexts/UserContext";

type ShopRow = {
    shopId: string;
    shopUrl: string;
    accountId: string;
    path: string;
};

type SortDir = "asc" | "desc";

export default function SuperShopsPage() {
    const user = useUserContext();
    const [rows, setRows] = useState<ShopRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [sortDir, setSortDir] = useState<SortDir>("asc");
    const [dupOnly, setDupOnly] = useState(false);
    const [deletingPath, setDeletingPath] = useState<string | null>(null);
    const [errMsg, setErrMsg] = useState<string>("");

    const load = async () => {
        try {
            setErrMsg("");
            setLoading(true);
            const res = await apiGet("/api/super/shops");
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j?.error || `failed (${res.status})`);
            }
            const json = await res.json();
            setRows(json.shops ?? []);
        } catch (e: any) {
            console.error("load shops failed:", e);
            setRows([]);
            setErrMsg(e?.message || "load failed");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // shopUrl 重複数 map（全体 rows ベース）
    const dupMap = useMemo(() => {
        const m = new Map<string, number>();
        rows.forEach((r) => {
            const k = (r.shopUrl || "").trim();
            if (!k) return;
            m.set(k, (m.get(k) ?? 0) + 1);
        });
        return m;
    }, [rows]);

    const filtered = useMemo(() => {
        const kw = q.trim().toLowerCase();

        let base = rows;

        if (kw) {
            base = base.filter((r) => {
                return (
                    (r.shopUrl || "").toLowerCase().includes(kw) ||
                    (r.shopId || "").toLowerCase().includes(kw) ||
                    (r.accountId || "").toLowerCase().includes(kw)
                );
            });
        }

        if (dupOnly) {
            base = base.filter((r) => {
                const k = (r.shopUrl || "").trim();
                if (!k) return false;
                return (dupMap.get(k) ?? 0) > 1;
            });
        }

        return base;
    }, [rows, q, dupOnly, dupMap]);

    // ✅ shopUrl でソート（昇順/降順）
    const sorted = useMemo(() => {
        const arr = [...filtered];
        arr.sort((a, b) => {
            const au = (a.shopUrl || "").trim();
            const bu = (b.shopUrl || "").trim();
            const c1 = au.localeCompare(bu);
            if (c1 !== 0) return c1;
            // 同じ shopUrl のとき安定するように
            const c2 = (a.accountId || "").localeCompare(b.accountId || "");
            if (c2 !== 0) return c2;
            return (a.shopId || "").localeCompare(b.shopId || "");
        });
        if (sortDir === "desc") arr.reverse();
        return arr;
    }, [filtered, sortDir]);

    if (!user.superAdmin) return <div className="p-4 text-sm">forbidden</div>;

    // ✅ 削除（DELETE /api/super/shops/:accountId/:shopId）
    const deleteShop = async (r: ShopRow) => {
        const ok = window.confirm(
            `この shop を削除しますか？\n\nshopUrl: ${r.shopUrl}\nshopId: ${r.shopId}\naccountId: ${r.accountId}`
        );
        if (!ok) return;

        try {
            setErrMsg("");
            setDeletingPath(r.path);

            const res = await apiDelete(`/api/super/shops/${r.accountId}/${r.shopId}`);
            // ↑ apiClient に apiDelete があるならそれを使うのが理想。
            // ただ、まず動かすならこれでもOK（同一オリジン前提）。

            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j?.error || `delete failed (${res.status})`);
            }

            // 画面から即反映（再取得でもOK）
            setRows((prev) => prev.filter((x) => x.path !== r.path));
        } catch (e: any) {
            console.error("delete failed:", e);
            setErrMsg(e?.message || "delete failed");
        } finally {
            setDeletingPath(null);
        }
    };

    const sortLabel = sortDir === "asc" ? "↑" : "↓";

    return (
        <div className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
                <h1 className="text-lg font-bold">Shops 一覧</h1>

                <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-600 flex items-center gap-1">
                        <input
                            type="checkbox"
                            checked={dupOnly}
                            onChange={(e) => setDupOnly(e.target.checked)}
                        />
                        DUPだけ
                    </label>

                    <button
                        className="text-xs px-3 py-2 border rounded bg-white"
                        onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                        title="shopUrl で昇順/降順"
                    >
                        shopUrl ソート {sortLabel}
                    </button>

                    <input
                        className="border rounded px-3 py-2 text-sm w-80"
                        placeholder="shopUrl / shopId / accountId で検索"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />

                    <button
                        className="text-xs px-3 py-2 border rounded bg-white"
                        onClick={load}
                        disabled={loading}
                    >
                        再読み込み
                    </button>
                </div>
            </div>

            {errMsg && (
                <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-2 rounded">
                    {errMsg}
                </div>
            )}

            {loading ? (
                <div className="text-sm text-slate-500">loading...</div>
            ) : (
                <div className="text-sm text-slate-600">
                    {sorted.length} 件（全 {rows.length} 件）
                </div>
            )}


            <table className="min-w-full text-sm border-collapse border border-slate-200">
                <thead className="bg-slate-50">
                    <tr>
                        <th className="text-center p-2 border">shopUrl</th>
                        <th className="text-center p-2 border">shopId</th>
                        <th className="text-center p-2 border">accountId</th>
                        <th className="text-center p-2 border">path</th>
                        <th className="text-center p-2 border">操作</th>
                    </tr>
                </thead>

                <tbody>
                    {sorted.map((r) => {
                        const k = (r.shopUrl || "").trim();
                        const dup = k && (dupMap.get(k) ?? 0) > 1;
                        const isDeleting = deletingPath === r.path;

                        return (
                            <tr key={r.path} className={dup ? "bg-amber-50" : ""}>
                                <td className="p-2 border whitespace-nowrap text-left">
                                    {r.shopUrl || <span className="text-slate-400">(empty)</span>}
                                    {dup && <span className="ml-2 text-xs text-amber-700">DUP</span>}
                                </td>
                                <td className="p-2 border font-mono whitespace-nowrap text-left">{r.shopId}</td>
                                <td className="p-2 border font-mono whitespace-nowrap text-left">{r.accountId}</td>
                                <td className="p-2 border font-mono whitespace-nowrap text-left">{r.path}</td>
                                <td className="p-2 border whitespace-nowrap">
                                    <button
                                        className="text-xs px-2 py-1 border rounded bg-white"
                                        onClick={() => deleteShop(r)}
                                        disabled={isDeleting}
                                        title="この shop を削除"
                                    >
                                        {isDeleting ? "削除中..." : "削除"}
                                    </button>
                                </td>
                            </tr>
                        );
                    })}

                    {!loading && sorted.length === 0 && (
                        <tr>
                            <td className="p-3 text-slate-500" colSpan={5}>
                                no data
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>


            <div className="text-xs text-slate-500">
                ※ Firestore の「doc delete」は subcollection を自動削除しません。必要なら API 側で access_rights なども消してください。
            </div>
        </div>
    );
}
